<template>
  <modal :name="modalName" :width="960" :height="'auto'" :scrollable="true" :adaptive="true">
    <div class="aoe">
      <!-- Header -->
      <div class="aoe-header">
        <div class="aoe-header__left">
          <div class="aoe-header__icon">
            <fa-icon icon="bolt" />
          </div>
          <div>
            <div class="aoe-header__eyebrow">{{ form.id ? `Gatilho #${form.id}` : 'Novo gatilho' }}</div>
            <h3 class="aoe-header__title">{{ form.id ? form.nome || 'Editar gatilho' : 'Criar novo gatilho' }}</h3>
          </div>
        </div>
        <div class="aoe-header__actions">
          <button
            v-if="form.id"
            type="button"
            class="aoe-btn aoe-btn--ghost aoe-btn--sm"
            @click="abrirSecundario('execucoes')"
            title="Ver execuções"
          >
            <fa-icon icon="history" /> Execuções
          </button>
          <button
            v-if="form.id"
            type="button"
            class="aoe-btn aoe-btn--ghost aoe-btn--sm"
            @click="abrirSecundario('auditoria')"
            title="Ver auditoria"
          >
            <fa-icon icon="clipboard-list" /> Auditoria
          </button>
          <button
            v-if="form.id && form.condition_type === 'sql'"
            type="button"
            class="aoe-btn aoe-btn--ghost aoe-btn--sm"
            @click="abrirSecundario('historico')"
            title="Histórico SQL"
          >
            <fa-icon icon="code" /> Histórico SQL
          </button>
          <button type="button" class="aoe-close" @click="close" title="Fechar">
            <fa-icon icon="times" />
          </button>
        </div>
      </div>

      <div class="aoe-body">
        <!-- Propriedades -->
        <section :id="`aoe-sec-propriedades-${_uid}`" class="aoe-anchor-section">
          <header class="aoe-anchor-header">
            <h2 class="aoe-anchor-title">
              <span class="aoe-anchor-step">1</span> Propriedades
            </h2>
            <p class="aoe-anchor-subtitle">Identificação, evento e status do gatilho.</p>
          </header>

          <div class="aoe-section">
            <div class="aoe-section__header">
              <h5 class="aoe-section__title">Identificação</h5>
              <p class="aoe-section__hint">Defina nome, descrição e quando este gatilho deve disparar.</p>
            </div>

            <div class="form-group aoe-fg">
              <label class="aoe-label">Nome <span class="aoe-required">*</span></label>
              <input class="form-control aoe-input" v-model="form.nome" placeholder="Ex: Notificar gerente em pedidos acima de 10k" />
            </div>

            <div class="form-group aoe-fg">
              <label class="aoe-label">Descrição</label>
              <textarea class="form-control aoe-input" rows="2" v-model="form.descricao" placeholder="Explique brevemente o que este gatilho faz..."></textarea>
            </div>

            <div class="aoe-grid aoe-grid--props">
              <div class="form-group aoe-fg aoe-grid__evento">
                <label class="aoe-label">Evento <span class="aoe-required">*</span></label>
                <select class="form-control aoe-input" v-model="form.evento" @change="onEventoChange">
                  <option :value="''">— Selecione um evento —</option>
                  <optgroup
                    v-for="grupo in eventosAgrupados"
                    :key="grupo.entidade"
                    :label="grupo.label"
                  >
                    <option v-for="e in grupo.eventos" :key="e.slug" :value="e.slug">{{ e.nome }}</option>
                  </optgroup>
                </select>
              </div>
              <div class="form-group aoe-fg">
                <label class="aoe-label">Ordem</label>
                <input type="number" class="form-control aoe-input" v-model.number="form.ordem" />
              </div>
            </div>

            <div class="aoe-status-row">
              <label class="aoe-label aoe-label--inline">Status</label>
              <label class="aoe-switch">
                <input type="checkbox" v-model="form.ativo" />
                <span class="aoe-switch__track">
                  <span class="aoe-switch__thumb"></span>
                </span>
                <div class="aoe-switch__text">
                  <strong>{{ form.ativo ? 'Gatilho ativo' : 'Gatilho inativo' }}</strong>
                  <span>{{ form.ativo ? 'Disparará automaticamente quando o evento ocorrer.' : 'Permanecerá pausado até ser ativado.' }}</span>
                </div>
              </label>
            </div>
          </div>
        </section>

        <!-- Aviso quando ainda não há evento selecionado -->
        <div v-if="!form.evento" class="aoe-evento-required">
          <fa-icon icon="info-circle" />
          <span>Para selecionar Condições e Ações, selecione primeiro um evento</span>
        </div>

        <!-- Condições -->
        <section v-if="form.evento" :id="`aoe-sec-condicoes-${_uid}`" class="aoe-anchor-section">
          <header class="aoe-anchor-header aoe-anchor-header--with-control">
            <div>
              <h2 class="aoe-anchor-title">
                <span class="aoe-anchor-step">2</span> Condições
              </h2>
              <p class="aoe-anchor-subtitle">Defina quando o gatilho deve disparar.</p>
            </div>
            <div class="aoe-logica">
              <label class="aoe-logica__label">Lógica entre regras</label>
              <select class="form-control aoe-input aoe-logica__select" v-model="form.validar_todas_regras">
                <option :value="true">Todas (AND)</option>
                <option :value="false">Qualquer (OR)</option>
              </select>
            </div>
          </header>

          <AcaoObjetoNlPreview
            :condition-type="form.condition_type"
            :condition-visual="form.condition_visual"
            :condition-sql="form.condition_sql"
            :regras="form.regras"
            :fields="fields"
            :evento-nome="eventoNome"
            :validar-todas-regras="form.validar_todas_regras"
          />
          <AcaoObjetoCondicaoBuilder
            :condition-type.sync="form.condition_type"
            :condition-visual.sync="form.condition_visual"
            :condition-sql.sync="form.condition_sql"
            :condition-sql-connection.sync="form.condition_sql_connection"
            :fields="fields"
            :fields-loaded="fieldsLoaded"
            :operadores="operadores"
            :endpoint-base="endpointBase"
          />
        </section>

        <!-- Ações -->
        <section v-if="form.evento" :id="`aoe-sec-acoes-${_uid}`" class="aoe-anchor-section">
          <header class="aoe-anchor-header">
            <h2 class="aoe-anchor-title">
              <span class="aoe-anchor-step">3</span> Ações
            </h2>
            <p class="aoe-anchor-subtitle">Defina o que será executado quando as condições forem atendidas.</p>
          </header>

          <AcaoObjetoAcaoForm
            :acoes.sync="form.acoes"
            :tipos="acaoTipos"
            :event-fields="fields"
            :event-fields-schema="fieldsSchemaRaw"
            :event-relations-schema="relationsSchemaRaw"
          />
        </section>
      </div>

      <!-- Modal secundário: Execuções / Auditoria / Histórico SQL -->
      <modal :name="secondaryModalName" :width="900" :height="'auto'" :scrollable="true" :adaptive="true">
        <div class="aoe-secondary">
          <div class="aoe-secondary__header">
            <h4 class="aoe-secondary__title">
              <fa-icon :icon="secondaryIcon" /> {{ secondaryTitle }}
            </h4>
            <div class="aoe-secondary__tabs">
              <button
                type="button"
                class="aoe-secondary__tab"
                :class="{ 'is-active': secondaryView === 'execucoes' }"
                @click="secondaryView = 'execucoes'"
              >
                <fa-icon icon="history" /> Execuções
              </button>
              <button
                type="button"
                class="aoe-secondary__tab"
                :class="{ 'is-active': secondaryView === 'auditoria' }"
                @click="secondaryView = 'auditoria'"
              >
                <fa-icon icon="clipboard-list" /> Auditoria
              </button>
              <button
                v-if="form.condition_type === 'sql'"
                type="button"
                class="aoe-secondary__tab"
                :class="{ 'is-active': secondaryView === 'historico' }"
                @click="secondaryView = 'historico'"
              >
                <fa-icon icon="code" /> Histórico SQL
              </button>
            </div>
            <button type="button" class="aoe-close" @click="fecharSecundario" title="Fechar">
              <fa-icon icon="times" />
            </button>
          </div>
          <div class="aoe-secondary__body">
            <AcaoObjetoExecucoesTab
              v-if="form.id && secondaryView === 'execucoes'"
              :endpoint-base="endpointBase"
              :acao-id="form.id"
              @replay="onReplay"
              @use-payload="onUsePayload"
            />
            <AcaoObjetoAuditoriaTab
              v-if="form.id && secondaryView === 'auditoria'"
              :endpoint-base="endpointBase"
              :acao-id="form.id"
            />
            <AcaoObjetoHistoricoTab
              v-if="form.id && secondaryView === 'historico' && form.condition_type === 'sql'"
              :endpoint-base="endpointBase"
              :acao-id="form.id"
              @restore="onRestoreSql"
            />
          </div>
        </div>
      </modal>

      <!-- Painel de teste (dry-run) -->
      <AcaoObjetoTestPanel
        ref="testPanel"
        :endpoint-base="endpointBase"
        :acao-id="form.id"
        :evento="form.evento"
        :fields="fields"
        :condicao-override="condicaoOverride"
      />

      <div class="aoe-footer">
        <button type="button" class="aoe-btn aoe-btn--ghost" @click="close">Cancelar</button>
        <button type="button" class="aoe-btn aoe-btn--secondary" @click="abrirTeste">
          <fa-icon icon="vial" /> Testar gatilho
        </button>
        <button type="button" class="aoe-btn aoe-btn--primary" @click="salvar">
          <fa-icon icon="save" /> Salvar gatilho
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import { get } from '@/helpers/apiRequest';
import AcaoObjetoCondicaoBuilder from './AcaoObjetoCondicaoBuilder.vue';
import AcaoObjetoAcaoForm from './AcaoObjetoAcaoForm.vue';
import AcaoObjetoHistoricoTab from './AcaoObjetoHistoricoTab.vue';
import AcaoObjetoExecucoesTab from './AcaoObjetoExecucoesTab.vue';
import AcaoObjetoAuditoriaTab from './AcaoObjetoAuditoriaTab.vue';
import AcaoObjetoNlPreview from './AcaoObjetoNlPreview.vue';
import AcaoObjetoTestPanel from './AcaoObjetoTestPanel.vue';

const EXTRA_FIELDS_BY_EVENTO = {
  PedidoFaturado:  [{ name: 'valor_total', label: 'Valor total do pedido' }],
  PedidoCancelado: [{ name: 'valor_total', label: 'Valor total do pedido' }],
  PedidoLiberado:  [{ name: 'valor_total', label: 'Valor total do pedido' }],
  OrcamentoEnviadoPedido: [{ name: 'valor_total', label: 'Valor total do pedido' }],
};

const EMPTY = {
  id: null,
  nome: '',
  descricao: '',
  evento: '',
  entidade: null,
  ordem: 0,
  ativo: false,
  validar_todas_regras: true,
  condition_type: 'visual',
  condition_visual: { grupos: [] },
  condition_sql: '',
  condition_sql_connection: null,
  regras: [],
  acoes: [],
};

export default {
  name: 'AcaoObjetoEdit',
  components: {
    AcaoObjetoCondicaoBuilder,
    AcaoObjetoAcaoForm,
    AcaoObjetoHistoricoTab,
    AcaoObjetoExecucoesTab,
    AcaoObjetoAuditoriaTab,
    AcaoObjetoNlPreview,
    AcaoObjetoTestPanel,
  },
  props: {
    modalName: { type: String, default: 'acaoObjetoEdit' },
    endpointBase: { type: String, required: true },
    item: { type: Object, default: null },
    eventos: { type: Array, default: () => [] },
    acaoTipos: { type: Array, default: () => [] },
    operadores: { type: Array, default: () => [] },
    onSaved: { type: Function, default: () => {} },
    defaultEntidade: { type: String, default: null },
  },
  data() {
    return {
      form: this.makeForm(this.item),
      fields: [],
      fieldsSchemaRaw: [],
      relationsSchemaRaw: [],
      fieldsLoaded: false,
      secondaryView: 'execucoes',
    };
  },
  computed: {
    secondaryModalName() {
      return `${this.modalName}--secondary`;
    },
    secondaryTitle() {
      if (this.secondaryView === 'auditoria') return 'Auditoria';
      if (this.secondaryView === 'historico') return 'Histórico SQL';
      return 'Execuções';
    },
    secondaryIcon() {
      if (this.secondaryView === 'auditoria') return 'clipboard-list';
      if (this.secondaryView === 'historico') return 'code';
      return 'history';
    },
    /**
     * Override de condicao consumido pelo painel de teste (dry-run).
     * Reflete o estado atual do form em tempo real para que o teste
     * use as edicoes nao salvas.
     */
    condicaoOverride() {
      return {
        condition_type:           this.form.condition_type,
        condition_visual:         this.form.condition_visual,
        condition_sql:            this.form.condition_sql,
        condition_sql_connection: this.form.condition_sql_connection,
        validar_todas_regras:     this.form.validar_todas_regras,
        regras:                   this.form.regras,
      };
    },
    eventoNome() {
      const e = (this.eventos || []).find(x => x.slug === this.form.evento);
      return e ? e.nome : (this.form.evento || '');
    },
    eventosAgrupados() {
      // Agrupa eventos por entidade para o dropdown (Pedido, Orçamento, etc).
      // A ordem segue ENTIDADE_ORDEM_LABELS; entidades não mapeadas vão para "Outros".
      const ENTIDADE_ORDEM_LABELS = [
        ['pedido',      'Pedido'],
        ['orcamento',   'Orçamento'],
        ['agenda',      'Agenda'],
        ['cliente',     'Cliente / Lead'],
        ['colaborador', 'Colaborador'],
        ['sac',         'SAC / Ticket'],
        ['atendimento', 'Atendimento'],
        ['pagamento',   'Pagamento'],
        ['outros',      'Outros'],
      ];
      const buckets = {};
      (this.eventos || []).forEach(ev => {
        const key = (ev.entidade || 'outros').toLowerCase();
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(ev);
      });
      const out = [];
      const usados = new Set();
      ENTIDADE_ORDEM_LABELS.forEach(([key, label]) => {
        if (buckets[key] && buckets[key].length) {
          out.push({ entidade: key, label, eventos: buckets[key] });
          usados.add(key);
        }
      });
      // Entidades não mapeadas viram grupos extras no fim (ordem alfabética).
      Object.keys(buckets).filter(k => !usados.has(k)).sort().forEach(key => {
        out.push({
          entidade: key,
          label:    key.charAt(0).toUpperCase() + key.slice(1),
          eventos:  buckets[key],
        });
      });
      return out;
    },
  },
  watch: {
    item(v) {
      this.form = this.makeForm(v);
      if (this.$refs.testPanel) this.$refs.testPanel.fechar();
      this.loadFieldsForEvento();
    },
  },
  methods: {
    makeForm(item) {
      const base = JSON.parse(JSON.stringify(EMPTY));
      if (this.defaultEntidade) base.entidade = this.defaultEntidade;
      if (!item || (item.__new && Object.keys(item).length === 1)) return base;

      const cleaned = JSON.parse(JSON.stringify(item));
      delete cleaned.__new;
      delete cleaned.__opened;
      const fieldLabels = cleaned.__field_labels || null;
      delete cleaned.__field_labels;
      const merged = Object.assign({}, base, cleaned);
      if (fieldLabels) Object.defineProperty(merged, '__field_labels', { value: fieldLabels, enumerable: false });
      return merged;
    },
    coletarCamposCondicao() {
      const cv = this.form.condition_visual;
      if (!cv || !Array.isArray(cv.grupos)) return [];
      const out = [];
      cv.grupos.forEach(g => {
        (g.condicoes || []).forEach(c => { if (c.campo) out.push(c.campo); });
      });
      return out;
    },
    mergeExtraFields(fields) {
      const extraLabels = this.form.__field_labels || {};
      const known = new Set(fields.map(f => f.name));
      const extrasEvento = EXTRA_FIELDS_BY_EVENTO[this.form.evento] || [];
      extrasEvento.forEach(f => {
        if (!known.has(f.name)) {
          fields.push({ name: f.name, label: extraLabels[f.name] || f.label });
          known.add(f.name);
        }
      });
      this.coletarCamposCondicao().forEach(name => {
        if (!known.has(name)) {
          fields.push({ name, label: extraLabels[name] || name });
          known.add(name);
        }
      });
      Object.keys(extraLabels).forEach(name => {
        if (!known.has(name)) {
          fields.push({ name, label: extraLabels[name] });
          known.add(name);
        }
      });
      return fields;
    },
    onEventoChange() {
      this.loadFieldsForEvento();
    },
    loadFieldsForEvento() {
      this.fieldsLoaded = false;
      this.fields = [];
      this.fieldsSchemaRaw = [];
      this.relationsSchemaRaw = [];
      if (!this.form.evento) { this.fieldsLoaded = true; return; }
      get(`${this.endpointBase}/eventos/${this.form.evento}/campos`)
        .then(json => {
          this.fieldsSchemaRaw = Array.isArray(json.fields_schema) ? json.fields_schema : [];
          this.relationsSchemaRaw = Array.isArray(json.relations_schema) ? json.relations_schema : [];
          // Mantém type/entidade vindos do backend (fields_schema do evento) — o
          // CondicaoBuilder usa esses metadados para escolher o input correto e
          // evitar inferir tipo pelo nome do campo (operacaoId é string, não number).
          const base = this.fieldsSchemaRaw.map(f => ({
            name:     f.name,
            label:    f.label || f.name,
            type:     f.type || null,
            entidade: f.entidade || null,
            // `grupo` é a model/segmento atribuído pelo EventoCamposBuilder
            // (ex.: "Pedido", "Cliente do pedido"). Sem isso o select cai
            // no fallback "Outros".
            grupo:    f.grupo || null,
          }));
          this.fields = this.mergeExtraFields(base);
          this.fieldsLoaded = true;
        })
        .catch(() => {
          this.fields = this.mergeExtraFields([]);
          this.fieldsLoaded = true;
        });
    },
    salvar() {
      const erro = this.validar();
      if (erro) {
        this.$swal({ title: 'Não foi possível salvar', text: erro, icon: 'warning' });
        return;
      }
      this.$emit('save', this.form);
    },
    validar() {
      const f = this.form;
      if (!f.nome || !f.nome.trim()) return 'Informe o nome do gatilho.';
      if (!f.evento) return 'Selecione um evento na aba Propriedades.';

      if (f.condition_type === 'visual') {
        const grupos = (f.condition_visual && f.condition_visual.grupos) || [];
        const temCond = grupos.some(g => Array.isArray(g.condicoes) && g.condicoes.some(c => c.campo));
        if (!grupos.length || !temCond) return 'Adicione ao menos uma condição na seção Condições.';
      } else if (f.condition_type === 'sql') {
        if (!f.condition_sql || !f.condition_sql.trim()) return 'Informe o SQL da condição na seção Condições.';
      }

      const acoes = Array.isArray(f.acoes) ? f.acoes : [];
      if (!acoes.length) return 'Adicione ao menos uma ação na seção Ações.';
      const acaoSemTipo = acoes.findIndex(a => !a.acao_tipo_slug);
      if (acaoSemTipo !== -1) return `Selecione o tipo da ação #${acaoSemTipo + 1}.`;

      return null;
    },
    close() {
      if (this.$refs.testPanel) this.$refs.testPanel.fechar();
      this.fecharSecundario();
      this.$emit('close');
    },
    abrirSecundario(view) {
      this.secondaryView = view || 'execucoes';
      this.$modal.show(this.secondaryModalName);
    },
    fecharSecundario() {
      this.$modal.hide(this.secondaryModalName);
    },
    abrirTeste() {
      if (this.$refs.testPanel) this.$refs.testPanel.abrir();
    },
    onReplay(log) {
      if (!log || !this.$refs.testPanel) return;
      this.$refs.testPanel.replayWith(log.event_data);
    },
    onUsePayload(log) {
      if (!log || !this.$refs.testPanel) return;
      this.$refs.testPanel.usePayload(log.event_data);
    },
    onRestoreSql(versao) {
      if (!versao || !versao.condition_sql) return;
      this.$swal({
        title: 'Restaurar versão SQL?',
        text: 'O conteúdo atual será substituído. Salve para confirmar.',
        showCancelButton: true,
        icon: 'warning',
        confirmButtonText: 'Restaurar',
        cancelButtonText: 'Cancelar',
      }).then(res => {
        if (!res.value) return;
        this.form.condition_sql = versao.condition_sql;
      });
    },
  },
};
</script>

<style scoped>
.aoe {
  background: #fff;
  display: flex;
  flex-direction: column;
}

/* Header */
.aoe-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px;
  background: linear-gradient(135deg, #f9fafb 0%, #f2f2f8 100%);
  border-bottom: 1px solid #e4e7eb;
}
.aoe-header__left { display: flex; align-items: center; gap: 14px; }
.aoe-header__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #3a3e81 0%, #5b60a6 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 4px 10px rgba(58, 62, 129, 0.25);
}
.aoe-header__eyebrow {
  font-size: 11px;
  font-weight: 600;
  color: #3a3e81;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.aoe-header__title {
  margin: 2px 0 0;
  font-size: 18px;
  font-weight: 700;
  color: #1d2939;
}
.aoe-close {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e4e7eb;
  background: #fff;
  color: #667085;
  cursor: pointer;
  transition: all 0.15s ease;
}
.aoe-close:hover { background: #d9534f; color: #fff; border-color: #d9534f; }

/* Body com scroll único */
.aoe-body {
  padding: 18px 22px 4px;
}

/* Header actions */
.aoe-header__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Anchor sections (fluxo principal vertical) */
.aoe-anchor-section {
  margin-bottom: 22px;
  scroll-margin-top: 16px;
}
.aoe-anchor-header {
  margin: 0 0 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid #eef0f7;
}
.aoe-anchor-header--with-control {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.aoe-anchor-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1d2939;
}
.aoe-anchor-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3a3e81 0%, #5b60a6 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(58, 62, 129, 0.25);
}
.aoe-anchor-subtitle {
  margin: 4px 0 0 34px;
  font-size: 12px;
  color: #667085;
}

/* Lógica entre regras (header de Condições) */
.aoe-logica {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f7f7fc;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  padding: 6px 10px;
}
.aoe-logica__label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  white-space: nowrap;
}
.aoe-logica__select {
  width: auto;
  min-width: 150px;
  height: auto;
  padding: 4px 8px;
  font-size: 12.5px;
}

/* Sections */
.aoe-section {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 14px;
}
.aoe-section__header {
  margin-bottom: 12px;
}
.aoe-section__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #1d2939;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.aoe-section__hint {
  margin: 2px 0 0;
  font-size: 12px;
  color: #667085;
}

/* Form */
.aoe-fg { margin-bottom: 12px; }
.aoe-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 6px;
}
.aoe-required { color: #d9534f; }
.aoe-input {
  border-radius: 6px;
  border: 1px solid #dddce9;
  font-size: 13px;
  transition: all 0.15s ease;
}
.aoe-input:focus {
  border-color: #3a3e81;
  box-shadow: 0 0 0 3px rgba(58, 62, 129, 0.1);
}
.aoe-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1.4fr;
  gap: 12px;
}
.aoe-grid--props {
  grid-template-columns: 2fr 1fr;
}
@media (max-width: 768px) {
  .aoe-grid,
  .aoe-grid--props { grid-template-columns: 1fr; }
}

/* Status dentro da Identificação */
.aoe-status-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px dashed #e4e7eb;
}
.aoe-label--inline {
  margin-bottom: 0;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 11px;
  color: #667085;
}

/* Aviso de evento obrigatório */
.aoe-evento-required {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0 22px;
  padding: 14px 18px;
  background: #fffbf2;
  border: 1px dashed #f0d59b;
  border-radius: 10px;
  color: #735200;
  font-size: 13px;
  font-weight: 500;
}
.aoe-evento-required svg { color: #b85f00; }

/* Switch */
.aoe-switch {
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  margin: 0;
}
.aoe-switch input { display: none; }
.aoe-switch__track {
  position: relative;
  width: 46px;
  height: 26px;
  background: #d0d5dd;
  border-radius: 14px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}
.aoe-switch__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}
.aoe-switch input:checked + .aoe-switch__track {
  background: #5eb95e;
}
.aoe-switch input:checked + .aoe-switch__track .aoe-switch__thumb {
  left: 23px;
}
.aoe-switch__text strong {
  display: block;
  font-size: 13px;
  color: #1d2939;
}
.aoe-switch__text span {
  font-size: 12px;
  color: #667085;
}

/* Footer */
.aoe-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 22px;
  background: #f9fafb;
  border-top: 1px solid #e4e7eb;
  margin-top: 8px;
}
.aoe-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s ease;
  font-family: inherit;
}
.aoe-btn--ghost {
  background: #fff;
  border-color: #dddce9;
  color: #475467;
}
.aoe-btn--ghost:hover { background: #f7f7fc; color: #1d2939; }
.aoe-btn--secondary {
  background: #edf8f9;
  color: #2298a3;
  border-color: #c5e6e9;
}
.aoe-btn--secondary:hover { background: #2298a3; color: #fff; border-color: #2298a3; }
.aoe-btn--primary {
  background: #3a3e81;
  color: #fff;
  box-shadow: 0 2px 6px rgba(58, 62, 129, 0.25);
}
.aoe-btn--primary:hover {
  background: #2f326b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(58, 62, 129, 0.3);
}
.aoe-btn[disabled] { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
.aoe-btn--sm {
  padding: 6px 12px;
  font-size: 12px;
}

/* Modal secundário (Execuções / Auditoria / Histórico SQL) */
.aoe-secondary { background: #fff; display: flex; flex-direction: column; }
.aoe-secondary__header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f9fafb 0%, #f2f2f8 100%);
  border-bottom: 1px solid #e4e7eb;
}
.aoe-secondary__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1d2939;
  white-space: nowrap;
}
.aoe-secondary__tabs {
  display: flex;
  gap: 4px;
  flex: 1;
  margin-left: 12px;
  flex-wrap: wrap;
}
.aoe-secondary__tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  background: #fff;
  color: #475467;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}
.aoe-secondary__tab:hover { background: #f7f7fc; color: #1d2939; }
.aoe-secondary__tab.is-active {
  background: #3a3e81;
  color: #fff;
  border-color: #3a3e81;
  box-shadow: 0 2px 6px rgba(58, 62, 129, 0.25);
}
.aoe-secondary__body { padding: 16px 18px; }

/* Painel de teste e estilos relacionados foram extraidos para AcaoObjetoTestPanel.vue */
</style>
